import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toastService from '../../services/toast';
import { useDispatch, useSelector } from 'react-redux';
import { addToCart } from '../../store/slices/cartSlice';
import axiosInstance from '../../config/axios';
import { ordersApi } from '../../services/api/orders.api';
import ShareModal from '../common/ShareModal';

// نستخدم API فقط بدون بيانات ثابتة

const CountdownTimer = ({ endDate }) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    const timer = setInterval(() => {
      const end = new Date(endDate).getTime();
      const now = new Date().getTime();
      const distance = end - now;

      setTimeLeft({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000)
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [endDate]);

  return (
    <span className="font-mono">
      {timeLeft.days}:{timeLeft.hours}:{timeLeft.minutes}:{timeLeft.seconds}
    </span>
  );
};

// مكون يعرض إشعار تسجيل الدخول للزوار غير المسجلين مع عرض وصف الدورة
const AuthRequiredPrompt = ({ courseTitle, courseDescription, whatYouWillLearn, sectionsCount, lessonsCount, sampleSections }) => {
  return (
    <div className="space-y-6">
      {/* وصف الدورة */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">وصف الدورة</h3>
        <div className="prose dark:prose-invert max-w-none" 
          dangerouslySetInnerHTML={{ __html: courseDescription }}
        />
        
        {whatYouWillLearn?.length > 0 && (
          <div className="mt-6">
            <h4 className="text-lg font-semibold text-gray-800 dark:text-white mb-3">ستتعلم في هذه الدورة</h4>
            <ul className="grid gap-2">
              {whatYouWillLearn.map((item, index) => (
                <li key={index} className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 dark:text-green-400 mt-0.5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* إشعار تسجيل الدخول */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
        <div className="flex flex-col items-center text-center">
          <div className="text-blue-700 dark:text-blue-400 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">استكشف محتوى الدورة الكامل</h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4">هذه الدورة تحتوي على <strong>{sectionsCount}</strong> قسم و <strong>{lessonsCount}</strong> درس.</p>
          <p className="text-gray-600 dark:text-gray-300 mb-4">لمشاهدة المحتوى الكامل والاستفادة من جميع ميزات الدورة، يرجى تسجيل الدخول أو إنشاء حساب جديد.</p>
          
          {sampleSections?.length > 0 && (
            <div className="w-full mt-4 border-t border-blue-200 dark:border-blue-700 pt-4">
              <h4 className="font-medium text-gray-800 dark:text-white mb-2">أقسام الدورة تشمل:</h4>
              <ul className="text-gray-600 dark:text-gray-300 list-disc list-inside text-right">
                {sampleSections.map(section => (
                  <li key={section.id}>{section.title}</li>
                ))}
                {sampleSections.length < sectionsCount && <li>والمزيد من الأقسام...</li>}
              </ul>
            </div>
          )}
          
          <div className="mt-6 flex flex-wrap justify-center gap-4">
            <a href="/login" className="bg-primary hover:bg-primary-dark text-white font-bold py-2 px-6 rounded-lg">
              تسجيل الدخول
            </a>
            <a href="/register" className="bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-bold py-2 px-6 rounded-lg">
              إنشاء حساب جديد
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

const CourseDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('overview');
  const [openSection, setOpenSection] = useState(null);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [realPrice, setRealPrice] = useState(null);
  const [realDiscountedPrice, setRealDiscountedPrice] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [courseThumbnail, setCourseThumbnail] = useState(null);
  const [courseTitle, setCourseTitle] = useState('');
  const [courseInstructor, setCourseInstructor] = useState({ name: '', bio: '', image: '' });
  const [hasInstructor, setHasInstructor] = useState(false); // حالة محتوى الدورة
  const [courseContent, setCourseContent] = useState([]);
  
  // متغيرات للمستخدمين غير المسجلين
  const [requiresAuth, setRequiresAuth] = useState(false);
  const [sectionsCount, setSectionsCount] = useState(0);
  const [lessonsCount, setLessonsCount] = useState(0);
  const [sampleSections, setSampleSections] = useState([]);
  const [courseDescription, setCourseDescription] = useState('');
  const [whatYouWillLearn, setWhatYouWillLearn] = useState([]);
  
  // helper
  const isSectionPublished = (section) => {
    return section?.status === 'published' ||
           section?.status === 'active' ||
           section?.published === true ||
           section?.is_published === true;
  };

  // جلب محتوى الدورة - استخدام مسار واحد للجميع
  const fetchCourseContent = async () => {
    try {
      // تأكد من وجود معلومات المستخدم
      const token = localStorage.getItem('token');
      const user = localStorage.getItem('user');
      
      // التحقق من صلاحيات المستخدم مبكراً
      const isCurrentUserAdmin = auth?.user?.role === 'admin';
      const isCurrentUserInstructor = auth?.user?.id === courseInstructor?.id;
      const isUserAuthenticated = !!token && !!user;

      // التحقق من حالة المستخدم وصلاحياته
      
      // استخدام مسار واحد للجميع مع إرسال معلومات الصلاحيات
      const endpoint = `/api/courses/${id}/content`;
      // استخدام مسار موحد لجلب محتوى الدورة
      
      // إرسال معلومات الصلاحيات في الهيدر
      const headers = { 'X-Public-Access': 'true' };
      
      // إذا كان المستخدم مشرف أو مدرس، نضيف معلومات الصلاحيات
      if (isUserAuthenticated && (isCurrentUserAdmin || isCurrentUserInstructor)) {
        headers['X-Admin-Access'] = 'true';
        headers['X-Instructor-ID'] = courseInstructor?.id || '';
      }
      
      const response = await axiosInstance.get(endpoint, { 
        params: { _t: Date.now() },
        headers: headers
      });
      
      // استلام بيانات المحتوى من الخادم

      const data = response.data;
      let content = [];
      
      if (data && data.sections) {
        content = data.sections;
      } else if (data && data.content) {
        content = data.content;
      } else if (Array.isArray(data)) {
        content = data;
      }
      
      // التحقق من وجود محتوى في الأقسام
      if (content && content.length > 0) {
        // الأقسام تحتوي على دروس، اختبارات، حقائب وقروبات
      }

      // معالجة المحتوى للتأكد من وجود مصفوفة items في كل قسم
      const processedContent = content.filter(isSectionPublished).map(section => {
        // إذا كانت مصفوفة items موجودة بالفعل، نستخدمها كما هي
        if (section.items && section.items.length > 0) {
          return section;
        }
        
        // إذا لم تكن موجودة، نقوم بتجميع جميع أنواع المحتوى في مصفوفة items
        const items = [
          ...((section.lessons || []).map(lesson => ({ ...lesson, type: lesson.type || 'lesson' }))),
          ...((section.exams || []).map(exam => ({ ...exam, type: 'quiz' }))),
          ...((section.resources || []).map(resource => ({ ...resource, type: 'course' }))),
          ...((section.groups || []).map(group => ({ ...group, type: 'group' })))
        ];
        
        // ترتيب العناصر حسب حقل order إذا كان موجوداً
        const sortedItems = items.sort((a, b) => {
          // إذا كان لديهما حقل order، نرتب حسبه
          if (a.order !== undefined && b.order !== undefined) {
            return a.order - b.order;
          }
          // إذا كان لديهما حقل sort_order، نرتب حسبه
          if (a.sort_order !== undefined && b.sort_order !== undefined) {
            return a.sort_order - b.sort_order;
          }
          // إذا لم يكن لديهما حقل للترتيب، نرتب حسب المعرف
          return (a.id || 0) - (b.id || 0);
        });
        
        // إضافة مصفوفة items المرتبة إلى القسم
        return { ...section, items: sortedItems };
      });
      
      setCourseContent(processedContent);
    } catch (err) {
      // فشل في جلب محتوى الدورة
      setCourseContent([]);
    }
  };

  // التحقق من اشتراك المستخدم في الدورة
  useEffect(() => {
    // استرجاع حالة التفضيل من الجلسة
    const storedFavorite = sessionStorage.getItem(`favorite_course_${id}`);
    if (storedFavorite !== null) {
      setIsFavorite(JSON.parse(storedFavorite));
    }

    // التحقق من تسجيل دخول المستخدم بوجود token في sessionStorage
    const token = sessionStorage.getItem('token');
    
    if (!token) {
      setIsEnrolled(false);
      return;
    }
    
    // التحقق من وجود البيانات في sessionStorage
    const cachedCourses = sessionStorage.getItem('dose_user_courses');
    
    if (cachedCourses) {
      try {
        const parsedCourses = JSON.parse(cachedCourses);
        // التحقق مما إذا كان المستخدم مشترك في هذه الدورة
        setIsEnrolled(parsedCourses.includes(parseInt(id)));
      } catch (err) {
        // خطأ في تحليل بيانات الدورات المخزنة
        setIsEnrolled(false);
      }
    } else {
      setIsEnrolled(false);
    }
  }, [id]);

  // حالة انتهاء الدورة
  const [isExpired, setIsExpired] = useState(false);
  const [isComingSoon, setIsComingSoon] = useState(false);
  const [installmentBlocked, setInstallmentBlocked] = useState(false);
  const [orderIdForInstallment, setOrderIdForInstallment] = useState(null);

  // دالة جلب بيانات الدورة
  useEffect(() => {
    // إضافة تأخير بسيط لمنع الطلبات المتكررة
    let isMounted = true;
    
    // دالة جلب بيانات الدورة
    const fetchCourseData = async () => {
      if (!isMounted) return;
      setIsLoading(true); // بداية التحميل
      
      // التحقق من صلاحيات المستخدم قبل اختيار المسار
      const isCurrentUserAdmin = auth?.user?.role === 'admin';
      
      try {
        // نختار المسار المناسب بناءً على صلاحيات المستخدم
        // (البيانات الأساسية مطلوبة للجميع، لكن المحتوى يختلف)
        const endpoint = isCurrentUserAdmin ? 
          `/api/admin/courses/${id}` : 
          `/api/courses/${id}`;
          
        // استخدام مسار البيانات الأساسية
        const response = await axiosInstance.get(endpoint);
        
        if (!isMounted) return;
        
        if (response.data) {
          // التحقق من حالة انتهاء الدورة
          setIsExpired(response.data.is_expired || false);
          setIsComingSoon(response.data.is_coming_soon_display || false);
          setInstallmentBlocked(response.data.installment_blocked || false);
          setOrderIdForInstallment(response.data.installment_overdue_order_id || null);
          // معالجة بيانات الدورة المستلمة
          
          // المعلومات الأساسية تعين دائماً للجميع (مسجلين وغير مسجلين)
          // تحويل السعر إلى رقم صحيح بدون كسور
          setRealPrice(response.data.price ? parseFloat(response.data.price) : null);
          setRealDiscountedPrice(response.data.discounted_price || null);
          
          // جلب عنوان الدورة والوصف
          setCourseTitle(response.data.name || '');
          setCourseDescription(response.data.description || '');
          // معالجة حقل ما ستتعلمه ليكون مصفوفة
          const learnRaw = response.data.whatYouWillLearn || response.data.what_you_will_learn || '';
          let learnArray = [];
          if (Array.isArray(learnRaw)) {
            learnArray = learnRaw;
          } else if (typeof learnRaw === 'string') {
            // نحاول استخراج العناصر من HTML أو نص عادي
            // إذا كان HTML <li>
            const tmp = learnRaw
              .replace(/<[^>]+>/g, '\n') // إزالة الوسوم وإبقاء فواصل
              .split(/\n|\r|\u2028|\u2029/)
              .map(s => s.trim())
              .filter(Boolean);
            learnArray = tmp;
          }
          setWhatYouWillLearn(learnArray);
          
          // جلب صورة الغلاف
          if (response.data.cover_image) {
            // تعيين صورة الغلاف للدورة
            setCourseThumbnail(response.data.cover_image);
          }
          
          // جلب بيانات المدرب (للجميع)
          if (response.data.instructors && response.data.instructors.length > 0) {
            const instructor = response.data.instructors[0];
            setCourseInstructor({
              name: instructor.name || '',
              bio: instructor.bio || '',
              image: instructor.image || ''
            });
            
            // التحقق مما إذا كان المدرب متاح للأسئلة
            const askInstructorValue = response.data.ask_instructor;
            setHasInstructor(askInstructorValue !== "disabled" && askInstructorValue !== null && askInstructorValue !== false);
          } else {
            setHasInstructor(false);
          }
          
          // التحقق من حالة تسجيل الدخول (من الاستجابة)
          const requiresAuth = response.data.requires_auth === true;
          // التحقق من ما إذا كانت الدورة تتطلب تسجيل دخول
          setRequiresAuth(requiresAuth);
          
          // التعامل مع بيانات المحتوى بناءً على حالة المستخدم
          if (requiresAuth) {
            // المستخدم غير مسجل دخول، سنحفظ معلومات محدودة
            setSectionsCount(response.data.sections_count || 0);
            setLessonsCount(response.data.lessons_count || 0);
            setSampleSections(response.data.sample_sections || []);
            setCourseContent([]);  // المحتوى التفصيلي فارغ للزوار
          } else {
            // المستخدم مسجل دخول، نحصل على المحتوى الكامل
            const content = response.data.sections || response.data.content || [];
            const publishedContent = content.filter(isSectionPublished);
            setCourseContent(publishedContent);

            // جلب محتوى الدورة بعد تحديد البيانات الأساسية
            // (محتوى الدورة يتم جلبه بناءً على صلاحيات المستخدم في دالة fetchCourseContent)
            fetchCourseContent();
          }
        }
      } catch (err) {
        if (isMounted) {
          // خطأ في جلب بيانات الدورة
        }
      } finally {
        if (isMounted) {
          setIsLoading(false); // نهاية التحميل
        }
      }
    };

    // إضافة تأخير بسيط قبل الطلب
    const timer = setTimeout(() => {
      fetchCourseData();
    }, 100);

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [id]);
  
  // نجيب بيانات المستخدم من الريدكس
  const auth = useSelector((state) => state.auth);
  
  // التحقق من صلاحية مشاهدة جميع الدورات
  const userPermissions = sessionStorage.getItem('userPermissions');
  const hasViewAllCoursesPermission = userPermissions ? JSON.parse(userPermissions).includes('view_all_courses') : false;
  
  // التحقق من الصلاحيات الخاصة - نعتمد فقط على صلاحية view_all_courses
  const hasSpecialAccess = hasViewAllCoursesPermission;
  
  // للتوافق مع الكود القديم
  const isAdmin = hasSpecialAccess || auth?.user?.role === 'admin';
  
  // التحقق من صلاحيات المستخدم والمدرس
  
  // تعديل: التأكد من أن المستخدم مسجل دخول أولاً قبل اعتباره مدرس
  // التأكد من وجود المستخدم والمدرس ومطابقة الهوية
  const isInstructor = Boolean(auth?.user?.id) && Boolean(courseInstructor?.id) && auth?.user?.id === courseInstructor?.id;

  const toggleSection = (sectionId) => {
    setOpenSection(openSection === sectionId ? null : sectionId);
  };

  const handleLessonClick = async (lesson) => {
    
    const lessonId = lesson.id || lesson._id;
    const isFree = lesson.is_free === true || lesson.is_free === 1 || lesson.is_free === '1';
    const lessonType = lesson.type || 'text';

    // التحقق من نوع وبيانات المحتوى

    // لو مدير أو مدرس الدورة يعدي على طول لأي محتوى
    if (isAdmin || isInstructor) {
      try {
        const hintType = (lessonType === 'course') ? 'resource' : lessonType;
        sessionStorage.setItem('learn_target_type', hintType);
      } catch (_) {}
      navigate(`/courses/${id}/learn/${lessonId}`);
      return;
    }

    // التحقق من حالة تسجيل الدخول
    const isLoggedIn = Boolean(sessionStorage.getItem('token'));

    // التحقق من حالة تسجيل الدخول للجميع
    if (!isLoggedIn) {
      toastService.error('يجب تسجيل الدخول أولاً');
      return;
    }
    
    // التحقق من انتهاء الدورة قبل أي شيء
    if (isExpired) {
      toastService.error('هذه الدورة منتهية ولا يمكن الوصول إلى محتواها');
      return;
    }
    
    // إذا كان المحتوى مجاني، نسمح بالدخول مباشرة
    if (isFree) {
      try {
        const hintType = (lessonType === 'course') ? 'resource' : lessonType;
        sessionStorage.setItem('learn_target_type', hintType);
      } catch (_) {}
      navigate(`/courses/${id}/learn/${lessonId}`);
      return;
    }
    
    // التحقق من الاشتراك في الدورة للمحتوى المدفوع
    try {
      // نستخدم API المشتريات للتحقق من شراء الدورة
      const orders = await ordersApi.getUserOrders('course');
      
      // نتحقق إذا كانت الدورة موجودة في قائمة المشتريات
      const hasPurchased = orders.some(order => 
        order.status === 'accepted' && 
        order.items.some(item => item.course_id?.toString() === id.toString())
      );
      
      
      // إذا كان المستخدم مشتري الدورة
      if (hasPurchased) {
        // المستخدم مشتري، نفتح له الدرس/المحتوى
        try {
          const hintType = (lessonType === 'course') ? 'resource' : lessonType;
          sessionStorage.setItem('learn_target_type', hintType);
        } catch (_) {}
        navigate(`/courses/${id}/learn/${lessonId}`);
      } else {
        // المستخدم غير مشترك، نعرض رسالة خطأ
        if (lessonType === 'course') {
          toastService.error('يجب الاشتراك في الدورة للوصول للحقيبة');
        } else if (lessonType === 'group') {
          toastService.error('يجب الاشتراك في الدورة للوصول للقروب');
        } else if (lessonType === 'quiz') {
          toastService.error('يجب الاشتراك في الدورة للوصول إلى الاختبار');
        } else {
          toastService.error('يجب الاشتراك في الدورة للوصول للمحتوى');
        }
      }
    } catch (error) {
      console.error('خطأ في التحقق من حالة الاشتراك:', error);
      toastService.error('حدث خطأ، يرجى المحاولة مرة أخرى');
    }
  };

  const handleEnrollClick = () => {
    // إضافة الدورة للسلة مباشرة
    const parsePrice = (priceStr) => {
      if (typeof priceStr === 'number') return priceStr;
      if (!priceStr) return 0;
      // إزالة أي حروف غير رقمية وتحويل لرقم
      const num = parseFloat(String(priceStr).replace(/[^0-9.]/g, ''));
      return isNaN(num) ? 0 : num;
    };

    const coursePrice = parsePrice(realDiscountedPrice || realPrice || 0);
    const originalPriceNum = parsePrice(realPrice || 0);

    const courseForCart = {
      id: id, // معرف الكورس من الرابط
      title: courseTitle,
      price: coursePrice,
      coverImage: courseThumbnail,
      type: 'course',
      instructor: courseInstructor?.name,
      originalPrice: originalPriceNum
    };
    
    // إضافة الدورة للسلة
    dispatch(addToCart(courseForCart));
    
    // التوجيه إلى صفحة السلة
    navigate('/cart');
  };

  const handleAskInstructor = () => {
    // زر سؤال المدرس تم النقر عليه
  };

  const handleFavorite = () => {
    const courseId = id;
    const newFavoriteState = !isFavorite;
    
    // تحديث الحالة في المكون
    setIsFavorite(newFavoriteState);
    
    // تخزين الحالة في الجلسة
    sessionStorage.setItem(`favorite_course_${courseId}`, JSON.stringify(newFavoriteState));
    
    // إظهار التوست المناسب
    toastService.success(newFavoriteState 
      ? 'تم إضافة الدورة إلى المفضلة'
      : 'تم إزالة الدورة من المفضلة'
    );
  };

  const handleShare = () => {
    setIsShareModalOpen(true);
  };

  const renderTabContent = () => {
    // Check if the course requires authentication first and user is not admin or instructor
    // التحقق من صلاحيات المستخدم لعرض المحتوى
    
    // تعديل: التأكد من أن المستخدم يرى المحتوى إذا كان requires_auth = false
    // أو إذا كان المستخدم مدير أو مدرس
    if (requiresAuth === true && !isAdmin && isInstructor !== true) {
      return (
        <AuthRequiredPrompt 
          courseTitle={courseTitle}
          courseDescription={courseDescription}
          whatYouWillLearn={whatYouWillLearn}
          sectionsCount={sectionsCount}
          lessonsCount={lessonsCount}
          sampleSections={sampleSections}
        />
      );
    }
    
    // For logged-in users, display regular content
    switch (activeTab) {
      case 'overview':
        return (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">وصف الدورة</h2>
            <div className="text-gray-600 dark:text-gray-400 mb-6" dangerouslySetInnerHTML={{ __html: courseDescription }}></div>

            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">ماذا ستتعلم ؟</h2>
            <ul className="space-y-2 mb-6">
              {whatYouWillLearn.map((point, index) => (
                <li key={index} className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-600 dark:text-gray-400">{point}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      case 'content':
      // عرض محتوى الدورة والتحقق من صلاحيات المستخدم
      
      // تحقق من البيانات المعروضة
      if (courseContent?.length === 0) {
        // لا يوجد محتوى متاح
      }
        
        // لو مقفولة بالتقسيط، نخفي المحتوى ونظهر بطاقة الدفع
        if (installmentBlocked) {
          return (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-red-200 dark:border-red-800 p-8 text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-lg bg-red-100 dark:bg-red-900/30 mb-4">
                <svg className="h-8 w-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">الوصول إلى محتوى الدورة معلّق مؤقتاً</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                عزيزي المشترك، تم تعليق صلاحية الوصول إلى محاضرات هذه الدورة مؤقتاً نظراً لوجود قسط مستحق لم يتم سداده. يمكنك استئناف المتابعة فوراً بعد إتمام عملية الدفع.
              </p>
              <a
                href={`/installment-details/${orderIdForInstallment}`}
                className="inline-block px-6 py-3 text-base font-medium text-white dark:text-white bg-[#0056b3] dark:bg-[#0056b3] rounded-lg hover:bg-[#004494] dark:hover:bg-[#004494] transition-colors"
              >
                ادفع الآن
              </a>
            </div>
          );
        }
        
        // لو مفيش محتوى نعرض رسالة
        if (!courseContent || courseContent.length === 0) {
          return (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-300 dark:border-gray-700 p-6 text-center">
              <p className="text-gray-600 dark:text-gray-400">لا يوجد محتوى متاح حالياً</p>
            </div>
          );
        }
        
        return (
          <div className="space-y-2 relative">
            {/* ختم منتهية للدورات المنتهية */}
            {isExpired && (
              <div className="absolute inset-0 flex items-center justify-center z-10 bg-black bg-opacity-40 rounded-lg">
                <div className="bg-red-600 text-white px-6 py-3 rounded-lg transform rotate-[-20deg] font-bold text-2xl border-2 border-white shadow-lg">
                  منتهية
                </div>
              </div>
            )}
            
            {courseContent.map((section, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 w-full">
                <button
                  onClick={() => toggleSection(index)}
                  className="w-full flex justify-between items-center p-3"
                >
                  <div className="flex items-center gap-4">
                    <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white font-semibold border border-gray-300 dark:border-gray-600">
                      {index + 1}
                    </span>
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">{section.title}</h3>
                  </div>
                  <svg
                    className={`w-5 h-5 text-gray-500 transition-transform ${openSection === index ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {openSection === index && (
                  <div className="border-t border-gray-300 dark:border-gray-700">
                    <div className="divide-y divide-gray-300 dark:divide-gray-700">
                      {/* عرض جميع أنواع المحتوى (الدروس) */}
                      {(section.items || []).map((lesson, idx) => (
                        <div key={idx} 
                          className="flex justify-between items-center p-3 hover:bg-blue-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer border-b border-transparent hover:border-blue-200 dark:hover:border-gray-600 hover:shadow-sm"
                          onClick={() => handleLessonClick(lesson)}
                        >
                          <div className="flex items-center gap-3">
                            {lesson.type === 'video' ? (
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                              </svg>
                            ) : lesson.type === 'quiz' ? (
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                                <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                              </svg>
                            ) : lesson.type === 'course' ? (
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-500" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
                              </svg>
                            ) : lesson.type === 'group' ? (
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-indigo-500" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                              </svg>
                            ) : (
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M4 4a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                              </svg>
                            )}
                            <span className="text-gray-700 dark:text-gray-200">{lesson.title}</span>
                          </div>
                          <div className="flex items-center">
                            {lesson.type === 'video' || lesson.type === 'lesson' ? (
                              <button className="text-blue-500 hover:text-blue-700 transition-colors">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                </svg>
                              </button>
                            ) : lesson.type === 'quiz' ? (
                              <button className="bg-green-100 text-green-700 hover:text-green-700 transition-colors px-2 py-1 text-xs rounded-full border border-green-500 hover:bg-green-50 dark:text-green-500 dark:bg-transparent dark:hover:bg-green-900/20">
                                ابدأ الاختبار
                              </button>
                            ) : lesson.type === 'course' || lesson.type === 'resource' ? (
                              <button className="bg-purple-100 text-purple-700 hover:text-purple-700 transition-colors px-2 py-1 text-xs rounded-full border border-purple-500 hover:bg-purple-50 dark:text-purple-500 dark:bg-transparent dark:hover:bg-purple-900/20">
                                استعراض
                              </button>
                            ) : lesson.type === 'group' ? (
                              <button className="bg-indigo-100 text-indigo-700 hover:text-indigo-700 transition-colors px-2 py-1 text-xs rounded-full border border-indigo-500 hover:bg-indigo-50 dark:text-indigo-500 dark:bg-transparent dark:hover:bg-indigo-900/20">
                                انضمام
                              </button>
                            ) : (
                              <button className="text-yellow-500 hover:text-yellow-700 transition-colors">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                  <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen" style={{marginTop: '-20px'}}>
      {/* تم إزالة شريط الخصم */}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-0" style={{marginTop: 0, paddingTop: 0}}>
        {/* Hero Section */}
        <div className="relative h-[200px] sm:h-[400px] overflow-hidden mb-6 mx-auto" style={{width: '100vw', marginLeft: 'calc(-50vw + 50%)', marginRight: 'calc(-50vw + 50%)'}}>
          <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
            {isLoading ? (
              <>
                {/* خلفية متحركة للتحميل - بنفس حجم ونسب الصورة */}
                <div className="absolute inset-0 w-full h-full bg-gray-200 dark:bg-gray-700 animate-pulse"></div>
              </>
            ) : (
              <>
                <img 
                  src={courseThumbnail}
                  alt={courseTitle}
                  className="w-full h-full object-contain"
                  loading="lazy"
                  width="1200"
                  height="400"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://placehold.co/1200x400?text=صورة+الدورة';
                  }}
                />
              </>
            )}
          </div>
          
          {/* طبقة تدرج سوداء شفافة دائمة بغض النظر عن حالة التحميل */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
          <div className="relative z-5 h-full flex items-end p-8 max-w-6xl mx-auto">
            <div>
              {isLoading ? (
                <>
                  {/* هيكل التحميل لمعلومات المدرب - للشاشات الكبيرة فقط */}
                  <div className="hidden sm:flex items-center gap-4 mb-4" style={{height: '64px'}}>
                    <div className="w-16 h-16 rounded-full bg-gray-300 dark:bg-gray-600 animate-pulse"></div>
                    <div className="flex flex-col justify-center">
                      <div className="h-5 w-32 bg-gray-300 dark:bg-gray-600 rounded animate-pulse mb-2"></div>
                      <div className="h-4 w-48 bg-gray-300 dark:bg-gray-600 rounded animate-pulse"></div>
                    </div>
                  </div>
                  
                  {/* هيكل التحميل لاسم المدرب - للموبايل فقط */}
                  <div className="sm:hidden mb-2" style={{height: '20px'}}>
                    <div className="h-4 w-24 bg-gray-300 dark:bg-gray-600 rounded animate-pulse"></div>
                  </div>
                  
                  {/* هيكل التحميل لعنوان الدورة */}
                  <div className="h-8 sm:h-10 w-3/4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" style={{marginBottom: '8px'}}></div>
                </>
              ) : (
                <>
                  {/* معلومات المدرب - تظهر فقط في الشاشات الكبيرة */}
                  <div className="hidden sm:flex items-center gap-4 mb-4">
                    <img 
                      src={courseInstructor.image}
                      alt={courseInstructor.name}
                      className="w-16 h-16 rounded-full border-2 border-white"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://placehold.co/200x200?text=المدرب';
                      }}
                    />
                    <div>
                      <h4 className="text-white text-lg font-bold">{courseInstructor.name}</h4>
                      <p className="text-white/90">{courseInstructor.bio}</p>
                    </div>
                  </div>
                  
                  {/* اسم المدرب في الموبايل فقط */}
                  <div className="sm:hidden mb-2">
                    <h4 className="text-white text-sm font-bold">{courseInstructor.name}</h4>
                  </div>
                  
                  {/* عنوان الدورة */}
                  <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2 drop-shadow-md leading-tight">{courseTitle}</h1>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <div className="flex-1">
            {/* Tabs Header */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-300 dark:border-gray-700">
              <div className="w-full">
                <div className="grid grid-cols-2">
                  <button 
                    onClick={() => setActiveTab('overview')}
                    className={`py-4 text-base font-medium transition-colors border-l border-gray-300 dark:border-gray-700 rounded-tr-lg rounded-br-lg ${
                      activeTab === 'overview' 
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white' 
                      : 'bg-white dark:bg-gray-800 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    نظرة عامة
                  </button>
                  <button 
                    onClick={() => setActiveTab('content')}
                    className={`py-4 text-base font-medium transition-colors rounded-tl-lg rounded-bl-lg ${
                      activeTab === 'content' 
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white' 
                      : 'bg-white dark:bg-gray-800 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    المحتوى
                  </button>
                </div>
              </div>
            </div>

            {/* Tab Content */}
            <div className="py-6">
              <div className="w-full">
                {renderTabContent()}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="w-full lg:w-[360px] lg:max-w-[360px]">
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-300 dark:border-gray-700 overflow-hidden sticky top-24">
              {isLoading ? (
                // حالة تحميل الصورة
                <div className="w-full h-[216px] bg-gray-200 dark:bg-gray-700 animate-pulse"></div>
              ) : (
                <div className="w-full h-[220px] relative overflow-hidden">
                  <img 
                    src={courseThumbnail}
                    alt={courseTitle}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width="600"
                    height="400"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://placehold.co/600x400?text=صورة+الدورة';
                    }}
                  />
                  <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-black/20 to-transparent"></div>
                  
                  {/* ختم الدورة المنتهية */}
                  {isExpired && (
                    <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
                      <div className="bg-black/60 text-white px-6 py-3 rounded-lg transform rotate-[-30deg] shadow-lg border-2 border-red-500">
                        <span className="text-xl font-bold">منتهية</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="p-6">
                {/* Price Section */}
                <div className="mb-6">
                  {isLoading ? (
                    // حالة التحميل
                    <div className="flex flex-col space-y-2">
                      <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                      <div className="h-5 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                    </div>
                  ) : (
                    // إخفاء السعر إذا كانت الدورة قريبًا
                    !isComingSoon && (realPrice !== null || realDiscountedPrice !== null) ? (
                      <>
                        <div className="flex items-center gap-3 mb-1">
                          <div className="text-2xl font-bold text-gray-900 dark:text-white">
                            {(realDiscountedPrice ?? realPrice)} <span style={{ fontFamily: 'saudi_riyal', fontSize: '1.2em' }}>&#xE900;</span>
                          </div>
                          {(realPrice !== null && realDiscountedPrice !== null && realDiscountedPrice < realPrice) && (
                            <div className="text-lg text-gray-500 dark:text-gray-400 line-through">
                              {realPrice} <span style={{ fontFamily: 'saudi_riyal', fontSize: '1.1em' }}>&#xE900;</span>
                            </div>
                          )}
                        </div>
                        {(realPrice !== null && realDiscountedPrice !== null && realDiscountedPrice < realPrice) && (
                          <div className="text-sm text-green-600 dark:text-green-400">
                            خصم {Math.round(((realPrice - realDiscountedPrice) / realPrice) * 100)}%
                          </div>
                        )}
                      </>
                    ) : null
                  )}
                </div>
                
                {/* Enrollment Button */}
                {isLoading ? (
                  // Skeleton loading للزر
                  <div className="w-full h-12 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse mb-4"></div>
                ) : (
                  <button 
                    onClick={handleEnrollClick}
                    disabled={isEnrolled || isExpired || isComingSoon}
                    className={`w-full py-3 px-4 rounded-lg font-medium mb-4 ${
                      isEnrolled
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 cursor-not-allowed'
                      : isExpired
                        ? 'bg-gray-400 text-white cursor-not-allowed'
                        : isComingSoon
                          ? 'bg-amber-500 text-white cursor-not-allowed'
                          : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    {isEnrolled ? 'تم التسجيل في الدورة' : isExpired ? 'منتهية' : isComingSoon ? 'قريبًا' : 'اشتري الآن'}
                  </button>
                )}

                {/* Course Features Section */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">مزايا الدورة</h3>
                  {isLoading ? (
                    // حالة التحميل لقسم المزايا
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <div className="w-5 h-5 mr-2 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse"></div>
                        <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                      </div>
                      <div className="flex items-center">
                        <div className="w-5 h-5 mr-2 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse"></div>
                        <div className="h-4 w-36 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                      </div>
                    </div>
                  ) : (
                    <ul className="space-y-2">
                      <li className="flex items-center">
                        <svg className="w-5 h-5 text-green-500 dark:text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                        <span className="text-gray-700 dark:text-gray-300">محتوى حصري</span>
                      </li>
                      {hasInstructor && (
                        <li className="flex items-center">
                          <svg className="w-5 h-5 text-green-500 dark:text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                          </svg>
                          <span className="text-gray-700 dark:text-gray-300">دعم مباشر من المدرب</span>
                        </li>
                      )}
                    </ul>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 mt-4">
                  <button 
                    onClick={handleFavorite}
                    className="flex-1 py-3 px-4 rounded-lg font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    <span className="flex items-center justify-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${isFavorite ? 'text-red-500' : 'text-gray-500 dark:text-gray-400'}`} viewBox="0 0 20 20" fill="currentColor">
                        <path d="M9.653 16.915l-.005-.003-.019-.01a20.759 20.759 0 01-1.162-.682 22.045 22.045 0 01-2.582-1.9C4.045 12.733 2 10.352 2 7.5a4.5 4.5 0 018-2.828A4.5 4.5 0 0118 7.5c0 2.852-2.044 5.233-3.885 6.82a22.049 22.049 0 01-3.744 2.582l-.019.01-.005.003h-.002a.739.739 0 01-.69.001l-.002-.001z" />
                      </svg>
                      مفضلة
                    </span>
                  </button>
                  <button 
                    onClick={handleShare}
                    className="flex-1 py-3 px-4 rounded-lg font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    <span className="flex items-center justify-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
                      </svg>
                      شارك
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        courseUrl={`https://dose.sa.com/courses/${id}`}
      />
    </div>
  );
};

export default CourseDetails;